import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import Barcode from "react-barcode";
import Drawer, { DrawerCloseButton } from "../Drawer";
import { LoaderSpinner } from "../LoaderSpinner";
import {
  fetchProductCatalogCatalogSkus,
  fetchProductCatalogCategorySettings,
  fetchProductCatalogPresetSettings,
  fetchProductCatalogChannelProducts,
  ProductCatalogLoadingKeys,
  syncMetafieldsToShopify,
} from "../../slices/productCatalogSlice";
import {
  clearVariantsByGroup,
  downloadAllBarcodes,
  getSuppliers,
  getVariantsByGroup,
  ProductGroupLoadingKeys,
} from "../../slices/productGroupSlice";
import { printProductBarcode, ProductLoadingKeys } from "../../slices/productsSlice";
import { enumInventoryPhasedStatus } from "../../consts/inventoryStatus";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  blockUnsignedIntegerKeyDown,
  sanitizeUnsignedIntegerString,
} from "../../utils/integerInput";
import { DatePickerInput } from "../DatePickerInput";
import { ConfrimModal } from "../ConfrimModal";
import { ShoppingBasketIcon } from "lucide-react";
import { ProductCatalogSelectFilter } from "./ProductCatalogSelectFilter";
import { formatShopifyGidForDisplay } from "./shopifyDisplay";
import {
  CatalogInfoTip,
  CATALOG_MFG_LEAD_TIME_INFO,
  CATALOG_TOTAL_LEAD_TIME_INFO,
} from "./CatalogInfoTip";
import { CatalogStatusChip } from "./CatalogStatusChip";
import SupplySku from "../SupplySku";

const EMPTY_TEXT = "-";
const EMPTY_CURRENCY = "$0.00";
const STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "IN_ACTIVE" },
  { label: "Sunset", value: "SUNSET" },
  { label: "Planned", value: "PLANNED" },
];

/** Same all/none copy as the catalog toolbar filters (`ProductCatalog.js`); used for trigger empty state when `showAllNone` is false. */
const SIDEBAR_SELECT_TRIGGER_LABELS = {
  categoryOptions: { allLabel: "All categories", noneLabel: "No categories" },
  subcategoryOptions: { allLabel: "All subcategories", noneLabel: "No subcategories" },
  brandPillarOptions: { allLabel: "All pillars", noneLabel: "No pillars" },
  demandTierOptions: { allLabel: "All tiers", noneLabel: "No tiers" },
  entryEngineOptions: { allLabel: "All entry engines", noneLabel: "No entry engines" },
  intentOptions: { allLabel: "All intents", noneLabel: "No intents" },
  containerGroupOptions: { allLabel: "All container groups", noneLabel: "No container groups" },
  vendorOptions: { allLabel: "All suppliers", noneLabel: "No supplier" },
  status: { allLabel: "", noneLabel: "Select status" },
};

const formatCatalogDateForInput = (v) => {
  if (v === null || v === undefined || v === "" || v === "-") return "";
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
};

const SIDEBAR_SHOPIFY_SYNC_FIELDS = new Set(["demand_tier_id", "intent_id"]);

const isSidebarShopifyNoneLabel = (value) => {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  if (s === "" || s === "-" ) return true;
  return s.toUpperCase() === "NONE";
};

const hasSidebarShopifyLinks = (item) => {
  const productId = String(item?.shopify_product_id || "").trim();
  const variantId = String(item?.shopify_variant_id || "").trim();
  return Boolean(productId && productId !== EMPTY_TEXT && variantId && variantId !== EMPTY_TEXT);
};

const SidebarShopifySyncButton = ({ isLoading, onClick }) => (
  <button
    type="button"
    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary text-white hover:bg-opacity-80 disabled:opacity-50"
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onClick?.();
    }}
    disabled={isLoading}
    title="Sync to Shopify"
  >
    {isLoading ? (
      <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
    ) : (
      <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
    )}
  </button>
);

const DATE_FIELDS_FOR_COMPARE = new Set(["launch_date", "sunset_date", "inactive_date"]);

/** Normalizes API / form values to sorted unique id strings for compare and pickers. */
export const parseChannelProductIds = (v) => {
  if (v === null || v === undefined || v === "" || v === "-") return [];
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") {
    const t = v.trim();
    if (!t || t === "-") return [];
    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x).trim()).filter(Boolean);
    } catch (_) {
      /* legacy single id */
    }
    return [t];
  }
  return [];
};

const channelProductIdsComparable = (v) =>
  JSON.stringify([...new Set(parseChannelProductIds(v))].sort());

const comparableForSidebarSave = (key, v) => {
  if (
    key === "fbm_channel_product_id" ||
    key === "fba_channel_product_id" ||
    key === "packaging_product_id"
  ) {
    return channelProductIdsComparable(v);
  }
  if (typeof v === "boolean") return v ? "1" : "0";
  if (DATE_FIELDS_FOR_COMPARE.has(key)) return formatCatalogDateForInput(v);
  if (v === null || v === undefined || v === "" || v === "-") return "";
  return String(v).trim();
};

/** Returns true when Save would send the same payload the server already has (skip API). */
const isCatalogSidebarSaveRedundant = (values, item) => {
  if (!item) return false;
  const editable = { ...values };
  delete editable.sell_on_shopify;
  delete editable.sell_on_amazon;
  delete editable.supplier;
  const next = {
    ...editable,
    category_group_id: values.category_group_id || null,
    subcategory_variant_id: values.subcategory_variant_id || null,
  };
  for (const key of Object.keys(next)) {
    const a = comparableForSidebarSave(key, next[key]);
    const b = comparableForSidebarSave(key, item[key]);
    if (a !== b) {
      return false;
    }
  }
  return true;
};

const normalizeNumberInput = (rawValue) => {
  let normalizedValue = String(rawValue ?? "").replace(",", ".");
  if (!/^\d*\.?\d*$/.test(normalizedValue)) return null;
  if (
    normalizedValue.includes(".") &&
    normalizedValue.startsWith("0") &&
    !normalizedValue.startsWith("0.")
  ) {
    normalizedValue = normalizedValue.replace(/^0+/, "") || "0";
  } else if (!normalizedValue.includes(".") && normalizedValue.length > 1) {
    normalizedValue = normalizedValue.replace(/^0+/, "") || "0";
  }
  return normalizedValue;
};

const DETAILS_SECTIONS = [
  {
    title: "Identity",
    rows: [
      { label: "Canonical Name", type: "text", field: "canonical_product_name" },
      { label: "Product Name", type: "text", field: "product_name" },
      { label: "Variant", type: "text", field: "variant_name" },
      { label: "Color", type: "input", field: "color", placeholder: "e.g. Navy" },
      { label: "Supply SKU", type: "text", field: "supply_sku" },
      { label: "Barcode", type: "text", field: "barcode" },
      { type: "barcode-panel" },
      { label: "Status", type: "select", field: "status", options: STATUS_OPTIONS },
      {
        label: "Packaging",
        description: "This SKU is a packaging component",
        type: "switcher",
        field: "is_packaging",
      },  
      {
        label: "Supplies",
        description: "This SKU is a supply or consumable",
        type: "switcher",
        field: "is_supply",
      },
    ],
  },
  {
    title: "Classification",
    rows: [
      { label: "Category", type: "select", field: "category_group_id", optionsKey: "categoryOptions" },
      { label: "Subcategory", type: "select", field: "subcategory_variant_id", optionsKey: "subcategoryOptions" },
      { label: "Brand Pillar", type: "select", field: "brand_pillar_id", optionsKey: "brandPillarOptions" },
      { label: "Demand Tier", type: "select", field: "demand_tier_id", optionsKey: "demandTierOptions" },
      { label: "Intent", type: "select", field: "intent_id", optionsKey: "intentOptions" },
      { label: "Entry Engine", type: "select", field: "entry_engine_id", optionsKey: "entryEngineOptions" },
    ],
  },
  {
    title: "Attributes",
    rows: [
      {
        label: "Material",
        type: "textarea",
        field: "material",
      },
    ],
  },
  {
    title: "Channel",
    rows: [
      {
        label: "FBM SKU",
        type: "channel-sku",
        field: "fbm_channel_product_id",
        skuField: "amazon_fbm_sku",
        excludeSiblingField: "fba_channel_product_id",
      },
      {
        label: "FBA SKU",
        type: "channel-sku",
        field: "fba_channel_product_id",
        skuField: "amazon_fba_sku",
        excludeSiblingField: "fbm_channel_product_id",
      },
      { label: "Shopify Product ID", type: "text", field: "shopify_product_id" },
      { label: "Shopify Variant ID", type: "text", field: "shopify_variant_id" },
    ],
  },
  {
    title: "Flags",
    rows: [
      {
        label: "Virtual Product",
        description: "Non-physical or bundled product",
        type: "switcher",
        field: "is_virtual",
      },
      {
        label: "Forecastable",
        description: "Include in demand forecasting",
        type: "switcher",
        field: "forecastable",
      },
      {
        label: "Needs Review",
        description: "Flag for manual review",
        type: "switcher",
        field: "needs_review",
      },
      {
        label: "Lock Flag",
        description: "Prevent further edits",
        type: "switcher",
        field: "lock_flag",
      },
      {
        label: "Replacement Exit",
        description: "This product replaces another",
        type: "switcher",
        field: "replacement_exit",
      },
      {
        label: "Replaces SKU",
        type: "catalog-sku",
        field: "replacement_product_id",
        skuField: "replaces_sku",
        showWhen: "replacement_exit",
      },
      {
        label: "Condition Version Of",
        description: "This SKU is the Outlet (or other condition) version of this regular SKU. Reclassify Inventory only moves stock along this link.",
        type: "catalog-sku",
        field: "condition_of_product_id",
        skuField: "condition_of_sku",
      },
      {
        label: "Sell on Shopify",
        type: "switcher",
        field: "sell_on_shopify",
        readOnly: true,
      },
      {
        label: "Sell on Amazon",
        type: "switcher",
        field: "sell_on_amazon",
        readOnly: true,
      },
    ]
  },
  {
    title: "Operations",
    rows: [
      {
        label: "Supplier",
        type: "vendor-select",
        field: "vendor_id",
        optionsKey: "vendorOptions",
      },
      {
        label: "Mfg Lead Time (days)",
        type: "input",
        field: "lead_time_days",
        placeholder: "0",
        integerOnly: true,
        labelInfoTip: CATALOG_MFG_LEAD_TIME_INFO,
      },
      { label: "Transit Time (days)", type: "input", field: "transit_lead_time_days", placeholder: "0", integerOnly: true },
      {
        label: "Total Lead Time (days)",
        type: "computed",
        field: "total_lead_time_days",
        labelInfoTip: CATALOG_TOTAL_LEAD_TIME_INFO,
      },
      { label: "MOQ", type: "input", field: "moq", placeholder: "0", integerOnly: true },
      { label: "Container Group", type: "select", field: "container_group_id", optionsKey: "containerGroupOptions" },
      { label: "Container QTY", type: "input", field: "container_qty", placeholder: "0", integerOnly: true },
      { label: "Carton QTY", type: "input", field: "carton_qty", placeholder: "0", integerOnly: true },
      { label: "Packaging SKU", type: "packaging-sku", field: "packaging_product_id", skuField: "packaging_sku" },
    ],
  },
  {
    title: "Physical",
    rows: [
      { label: "Weight (lbs)", type: "input", field: "unit_weight_lbs", placeholder: "0.00" },
      { label: "Length (in)", type: "input", field: "unit_length", placeholder: "0.00" },
      { label: "Width (in)", type: "input", field: "unit_width", placeholder: "0.00" },
      { label: "Height (in)", type: "input", field: "unit_height", placeholder: "0.00" },
      { label: "Cubic Inches", type: "computed", field: "cubic_inches" },
    ],
  },
  {
    title: "Lifecycle",
    rows: [
      { label: "Launch Date", type: "date", field: "launch_date" },
      { label: "Sunset Date", type: "date", field: "sunset_date" },
      { label: "Inactive Date", type: "date", field: "inactive_date" },
    ],
  },
  {
    title: "Finance",
    rows: [
      { label: "Product BOM Cost", type: "text", field: "product_bom_cost", isCurrency: true },
      { label: "Packaging BOM Cost", type: "text", value: EMPTY_CURRENCY, isCurrency: true },
      { label: "Landed Cost", type: "text", field: "landed_cost", isCurrency: true },
      { label: "Weighted Cost", type: "text", field: "weighted_cost", isCurrency: true },
    ],
  },
];

/** Read shell shared with inline inputs + Material + Replaces SKU (hover row + dotted underline on value). */
const INLINE_VALUE_READ_CLASS =
  "w-full cursor-pointer text-right text-sm outline-none select-text whitespace-pre-wrap break-words [text-decoration-skip-ink:none] rounded-sm transition-[text-decoration] underline-offset-[3px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:underline focus-visible:decoration-dotted focus-visible:decoration-gray-900/80 group-hover:underline group-hover:decoration-dotted group-hover:decoration-gray-900/70";

export const CatalogSkuPicker = ({ row, values, item, onChange }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectedId = values?.[row.field];
  const selectedSku = values?.[row.skuField] || item?.[row.skuField] || EMPTY_TEXT;

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !item?.id) return undefined;
    let cancelled = false;
    const delay = search.trim() ? 300 : 0;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await dispatch(
          fetchProductCatalogCatalogSkus({
            search: search.trim(),
            excludeProductId: item.id,
          })
        ).unwrap();
        if (!cancelled) {
          setOptions(result?.items || []);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isOpen, search, item?.id, dispatch]);

  const filteredOptions = options;

  const hasSelection =
    selectedId != null && selectedId !== "" && String(selectedId).trim() !== "";
  const displaySku =
    hasSelection && selectedSku && selectedSku !== EMPTY_TEXT ? String(selectedSku) : EMPTY_TEXT;

  const toggleOpen = () => {
    setSearch("");
    setIsOpen((prev) => !prev);
  };

  const clearSelection = () => {
    onChange?.(row.field, "");
    onChange?.(row.skuField, "");
  };

  return (
    <div className="relative flex min-w-0 flex-1 items-center justify-end gap-1.5">
      <button
        type="button"
        title="Select catalog SKU"
        className={`min-w-0 flex-1 truncate ${INLINE_VALUE_READ_CLASS} ${
          hasSelection ? "text-gray-800" : "text-gray-400"
        }`}
        onClick={toggleOpen}
      >
        {displaySku}
      </button>
      {hasSelection ? (
        <button
          type="button"
          aria-label={`Clear ${row.label}`}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
          onClick={(e) => {
            e.stopPropagation();
            clearSelection();
            setIsOpen(false);
          }}
        >
          <span className="text-[15px] leading-none font-medium text-gray-500">−</span>
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Search catalog SKUs"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
        onClick={(e) => {
          e.stopPropagation();
          toggleOpen();
        }}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full z-[200] mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="px-2 py-2 border-b border-gray-100">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Search catalog SKUs
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Type to search SKUs..."
                className="w-full rounded border border-gray-300 py-1 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto py-1">
            {loading && (
              <p className="px-3 py-2 text-xs text-gray-400">Loading...</p>
            )}
            {!loading &&
              filteredOptions.map((option) => {
                const skuLabel = option.sku && option.sku !== EMPTY_TEXT ? String(option.sku) : EMPTY_TEXT;
                const nameLabel =
                  option.name && option.name !== EMPTY_TEXT ? String(option.name) : "";
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onChange?.(row.field, option.id);
                      onChange?.(row.skuField, skuLabel);
                      setIsOpen(false);
                    }}
                    className={`w-full px-3 py-1.5 text-left hover:bg-gray-50 flex items-start justify-between gap-2 ${String(selectedId || "") === String(option.id) ? "bg-gray-50" : ""}`}
                  >
                    <span className="min-w-0 flex-1 flex flex-col items-start gap-0.5">
                      <span className="text-sm font-medium text-gray-900 truncate w-full">{skuLabel}</span>
                      {nameLabel ? (
                        <span className="text-xs text-gray-500 truncate w-full">{nameLabel}</span>
                      ) : null}
                    </span>
                    {String(selectedId || "") === String(option.id) && (
                      <span className="text-gray-500 shrink-0 pt-0.5">✓</span>
                    )}
                  </button>
                );
              })}
            {!loading && filteredOptions.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matching SKUs</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ChannelSkuPicker = ({ row, values, item, onChange, dynamicOptions, onOpenChange }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const rootRef = useRef(null);
  const options = dynamicOptions?.channelProductOptions || [];
  const allKnownOptions = useMemo(() => [...(options || []), ...(searchResults || [])], [options, searchResults]);
  const selectedIds = parseChannelProductIds(values?.[row.field]);
  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  const excludeSiblingIds = useMemo(() => {
    if (!row.excludeSiblingField) return new Set();
    return new Set(parseChannelProductIds(values?.[row.excludeSiblingField]).map(String));
  }, [row.excludeSiblingField, values]);

  const optionById = useMemo(() => {
    const m = new Map();
    allKnownOptions.forEach((opt) => {
      if (opt?.id != null) m.set(String(opt.id), opt);
    });
    return m;
  }, [allKnownOptions]);

  const fallbackSkuById = useMemo(() => {
    const labels = String(values?.[row.skuField] || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const map = new Map();
    selectedIds.forEach((id, index) => {
      if (labels[index]) map.set(String(id), labels[index]);
    });
    return map;
  }, [row.skuField, selectedIds, values]);

  const setSelection = (nextIds) => {
    const normalized = [...new Set(nextIds.map((id) => String(id).trim()).filter(Boolean))];
    onChange?.(row.field, normalized);
    const skuLine =
      normalized.length === 0
        ? ""
        : normalized
            .map((id) => optionById.get(String(id))?.sku || fallbackSkuById.get(String(id)) || "")
            .filter(Boolean)
            .join(", ");
    onChange?.(row.skuField, skuLine);
  };

  const toggleId = (optionId) => {
    const idStr = String(optionId);
    const next = new Set(selectedSet);
    if (next.has(idStr)) next.delete(idStr);
    else next.add(idStr);
    setSelection([...next]);
  };

  const removeId = (optionId) => {
    const idStr = String(optionId);
    setSelection(selectedIds.filter((id) => String(id) !== idStr));
  };

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocumentMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !search.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await dispatch(
          fetchProductCatalogCatalogSkus({
            search: search.trim(),
            excludeProductId: item?.id,
            includeNameMatch: true,
            includeBarcodeMatch: true,
          })
        ).unwrap();
        if (!cancelled) {
          setSearchResults(result?.items || []);
        }
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [dispatch, isOpen, item?.id, search]);

  const filteredOptions = useMemo(() => {
    const sourceOptions = search.trim() ? searchResults : options;
    const currentProductId = item?.id != null && item?.id !== "" ? String(item.id) : null;
    return (sourceOptions || []).filter((option) => {
      if (option?.id != null && excludeSiblingIds.has(String(option.id))) return false;
      if (currentProductId != null && option?.id != null && String(option.id) === currentProductId) {
        return false;
      }
      if (search.trim()) return true;
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        String(option?.sku || "").toLowerCase().includes(q) ||
        String(option?.name || "").toLowerCase().includes(q)
      );
    });
  }, [options, searchResults, excludeSiblingIds, search, item?.id]);

  return (
    <div ref={rootRef} className="relative flex max-w-[320px] flex-col items-end gap-1.5">
      <div className="flex w-full flex-wrap items-center justify-end gap-1">
        {selectedIds.length === 0 ? (
          <span className="text-sm text-gray-500">{EMPTY_TEXT}</span>
        ) : (
          selectedIds.map((id) => {
            const opt = optionById.get(String(id));
            const label = opt?.sku || fallbackSkuById.get(String(id)) || String(id);
            return (
              <span
                key={String(id)}
                className="inline-flex max-w-full items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-800"
              >
                <span className="truncate">{label}</span>
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                  onClick={() => removeId(id)}
                  aria-label={`Remove ${label}`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })
        )}
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
          onClick={() => {
            setSearch("");
            setIsOpen((prev) => !prev);
          }}
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute right-0 top-full z-[190] mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 px-2 py-2">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {search.trim() ? "Search all products" : `SKUs matching barcode ${item?.barcode || EMPTY_TEXT}`}
            </p>
            <p className="mb-1.5 text-[10px] text-gray-400">
              {search.trim()
                ? "Search by SKU, barcode, or product name. SKUs already chosen for the other fulfillment type are hidden here."
                : "Click to add or remove. SKUs already chosen for the other fulfillment type are hidden here."}
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter SKUs, barcode, name..."
                className="w-full rounded border border-gray-300 py-1 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto py-1">
            {searchLoading && <p className="px-3 py-2 text-xs text-gray-400">Loading...</p>}
            {!searchLoading && filteredOptions.map((option) => {
              const isOn = selectedSet.has(String(option.id));
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    toggleId(option.id);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${isOn ? "bg-gray-50 font-medium" : ""}`}
                >
                  <span className="truncate">{option.sku || EMPTY_TEXT}</span>
                  {isOn && <span className="text-gray-500">✓</span>}
                </button>
              );
            })}
            {!searchLoading && filteredOptions.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matching SKUs</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PackagingSkuPicker = ({ row, values, item, onChange, onOpenChange }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const selectedIds = parseChannelProductIds(values?.[row.field]);
  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  const optionById = useMemo(() => {
    const m = new Map();
    (options || []).forEach((opt) => {
      if (opt?.id != null) m.set(String(opt.id), opt);
    });
    return m;
  }, [options]);

  const fallbackSkuById = useMemo(() => {
    const labels = String(values?.[row.skuField] || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const map = new Map();
    selectedIds.forEach((id, index) => {
      if (labels[index]) map.set(String(id), labels[index]);
    });
    return map;
  }, [row.skuField, selectedIds, values]);

  const setSelection = (nextIds) => {
    const normalized = [...new Set(nextIds.map((id) => String(id).trim()).filter(Boolean))];
    onChange?.(row.field, normalized);
    const skuLine =
      normalized.length === 0
        ? ""
        : normalized
            .map((id) => optionById.get(String(id))?.sku || fallbackSkuById.get(String(id)) || "")
            .filter(Boolean)
            .join(", ");
    onChange?.(row.skuField, skuLine);
  };

  const toggleId = (optionId) => {
    const idStr = String(optionId);
    const next = new Set(selectedSet);
    if (next.has(idStr)) next.delete(idStr);
    else next.add(idStr);
    setSelection([...next]);
  };

  const removeId = (optionId) => {
    const idStr = String(optionId);
    setSelection(selectedIds.filter((id) => String(id) !== idStr));
  };

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocumentMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const delay = search.trim() ? 250 : 0;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await dispatch(
          fetchProductCatalogCatalogSkus({
            search: search.trim() || "BX-",
            excludeProductId: item?.id,
          })
        ).unwrap();
        if (!cancelled) {
          setOptions(
            (result?.items || []).filter((option) =>
              String(option?.sku || "").toUpperCase().includes("BX-")
            )
          );
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [dispatch, isOpen, item?.id, search]);

  return (
    <div ref={rootRef} className="relative flex max-w-[320px] flex-col items-end gap-1.5">
      <div className="flex w-full flex-wrap items-center justify-end gap-1">
        {selectedIds.length === 0 ? (
          <span className="text-sm text-gray-500">{EMPTY_TEXT}</span>
        ) : (
          selectedIds.map((id) => {
            const opt = optionById.get(String(id));
            const label = opt?.sku || fallbackSkuById.get(String(id)) || String(id);
            return (
              <span
                key={String(id)}
                className="inline-flex max-w-full items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-800"
              >
                <span className="truncate">{label}</span>
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                  onClick={() => removeId(id)}
                  aria-label={`Remove ${label}`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })
        )}
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
          onClick={() => {
            setSearch("");
            setIsOpen((prev) => !prev);
          }}
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute right-0 top-full z-[190] mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 px-2 py-2">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Packaging SKUs
            </p>
            <p className="mb-1.5 text-[10px] text-gray-400">SKUs containing BX-. Click to add or remove.</p>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter BX- SKUs..."
                className="w-full rounded border border-gray-300 py-1 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto py-1">
            {loading && <p className="px-3 py-2 text-xs text-gray-400">Loading...</p>}
            {!loading &&
              options.map((option) => {
                const isOn = selectedSet.has(String(option.id));
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      toggleId(option.id);
                      setIsOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${isOn ? "bg-gray-50 font-medium" : ""}`}
                  >
                    <span className="truncate">{option.sku || EMPTY_TEXT}</span>
                    {isOn && <span className="text-gray-500">✓</span>}
                  </button>
                );
              })}
            {!loading && options.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No BX- SKUs found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function getInlineInputReadDisplay(row, raw) {
  if (row.integerOnly) {
    const v =
      raw === null || raw === undefined || raw === "" || raw === EMPTY_TEXT ? null : raw;
    if (v === null || v === "") {
      return { text: EMPTY_TEXT, tone: "empty" };
    }
    return { text: String(v), tone: "value" };
  }
  if (row.field === "color") {
    const s = raw === null || raw === undefined || raw === EMPTY_TEXT ? "" : String(raw).trim();
    if (s === "") {
      if (row.placeholder) {
        return { text: row.placeholder, tone: "placeholder" };
      }
      return { text: EMPTY_TEXT, tone: "empty" };
    }
    return { text: s, tone: "value" };
  }
  const s = raw === null || raw === undefined || raw === EMPTY_TEXT ? "" : String(raw).trim();
  if (s === "") {
    return { text: EMPTY_TEXT, tone: "empty" };
  }
  return { text: s, tone: "value" };
}

const normalizeLeadTimePart = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sumLeadTimeDays = (mfgValue, transitValue) =>
  normalizeLeadTimePart(mfgValue) + normalizeLeadTimePart(transitValue);

const INLINE_INPUT_CLASS =
  "w-full max-w-[210px] rounded-md border border-gray-800/90 bg-white px-2.5 py-1.5 text-xs text-gray-800 text-right outline-none focus:ring-2 focus:ring-primary";

const SidebarInlineEditableInput = ({ row, values, onChange, item }) => {
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    setIsEditing(false);
  }, [item?.id]);

  const raw = values?.[row.field];
  const read = getInlineInputReadDisplay(row, raw);

  const readToneClass =
    read.tone === "placeholder"
      ? "text-gray-400 italic"
      : read.tone === "empty"
        ? "text-gray-400"
        : "text-gray-800";

  const openEdit = () => setIsEditing(true);

  if (!isEditing) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={`${INLINE_VALUE_READ_CLASS} ${readToneClass}`}
        onClick={openEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEdit();
          }
        }}
        title="Click to edit"
      >
        {read.text}
      </div>
    );
  }

  if (row.integerOnly) {
    return (
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        value={values?.[row.field] ?? ""}
        onKeyDown={(e) => {
          blockUnsignedIntegerKeyDown(e);
          if (e.key === "Escape") {
            e.preventDefault();
            setIsEditing(false);
          }
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
        onChange={(event) => {
          const sanitized = sanitizeUnsignedIntegerString(event.target.value);
          if (sanitized === "") {
            onChange?.(row.field, "");
            return;
          }
          const n = parseInt(sanitized, 10);
          if (!Number.isFinite(n) || n < 0) return;
          onChange?.(row.field, String(n));
        }}
        onBlur={() => setIsEditing(false)}
        className={INLINE_INPUT_CLASS}
        placeholder={row.placeholder || "0"}
      />
    );
  }

  if (row.field === "color") {
    return (
      <input
        type="text"
        autoFocus
        value={values?.[row.field] || ""}
        onChange={(event) => {
          onChange?.(row.field, event.target.value);
        }}
        onBlur={() => setIsEditing(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setIsEditing(false);
          }
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
        className={INLINE_INPUT_CLASS}
        placeholder={row.placeholder || ""}
      />
    );
  }

  return (
    <input
      type="text"
      autoFocus
      value={values?.[row.field] || ""}
      onChange={(event) => {
        const normalizedValue = normalizeNumberInput(event.target.value);
        if (normalizedValue === null) return;
        onChange?.(row.field, normalizedValue);
      }}
      onBlur={() => setIsEditing(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setIsEditing(false);
        }
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className={INLINE_INPUT_CLASS}
      placeholder={row.placeholder || "0.00"}
    />
  );
};

const SIDEBAR_TEXTAREA_EDIT_CLASS =
  "w-full min-h-[88px] resize-y rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary";

const SidebarDoubleClickTextarea = ({ row, values, onChange, item }) => {
  // const [isEditing, setIsEditing] = useState(false);
  // useEffect(() => {
  //   setIsEditing(false);
  // }, [item?.id]);

  const raw = values?.[row.field];
  const textValue = raw === null || raw === undefined || raw === EMPTY_TEXT ? "" : String(raw);
  // const isEmpty = textValue.trim() === "";
  // const displayText = isEmpty ? EMPTY_TEXT : textValue;

  // const openEdit = () => setIsEditing(true);

  // if (!isEditing) {
  //   return (
  //     <div
  //       role="button"
  //       tabIndex={0}
  //       className={`${INLINE_VALUE_READ_CLASS} ${isEmpty ? "text-gray-400" : "text-gray-800"}`}
  //       onClick={openEdit}
  //       onKeyDown={(e) => {
  //         if (e.key === "Enter" || e.key === " ") {
  //           e.preventDefault();
  //           openEdit();
  //         }
  //       }}
  //       title="Click to edit"
  //     >
  //       {displayText}
  //     </div>
  //   );
  // }

  return (
    <textarea
      className={SIDEBAR_TEXTAREA_EDIT_CLASS}
      placeholder={row.placeholder || ""}
      value={textValue}
      onChange={(event) => {
        onChange?.(row.field, event.target.value);
      }}
      // onBlur={() => setIsEditing(false)}
      // onKeyDown={(e) => {
      //   if (e.key === "Escape") {
      //     e.preventDefault();
      //     setIsEditing(false);
      //   }
      // }}
      rows={4}
      autoFocus
    />
  );
};

const ValueField = ({
  row,
  values,
  item,
  onChange,
  dynamicOptions = {},
  onCatalogSelectOpenChange,
}) => {
  if (row.type === "vendor-select") {
    const options = (dynamicOptions[row.optionsKey] || []).map((opt) => ({
      ...opt,
      value: String(opt.value),
    }));
    const rowValue =
      values?.[row.field] !== undefined && values?.[row.field] !== null && values?.[row.field] !== ""
        ? String(values[row.field])
        : "";
    const disabled = Boolean(dynamicOptions.vendorSelectLoading);
    const { allLabel, noneLabel } =
      SIDEBAR_SELECT_TRIGGER_LABELS.vendorOptions || { allLabel: "", noneLabel: EMPTY_TEXT };
    const emptyTriggerLabel = EMPTY_TEXT;
    const selectedForFilter =
      rowValue !== "" && rowValue !== null && rowValue !== undefined ? [String(rowValue)] : [];
    return (
      <div className="w-[210px] min-w-0">
        <ProductCatalogSelectFilter
          showAllNone={false}
          allLabel={emptyTriggerLabel}
          noneLabel={noneLabel}
          options={options}
          isLoading={disabled}
          disabled={disabled}
          selectedOptions={selectedForFilter}
          onChange={(next) => {
            const v = Array.isArray(next) && next.length > 0 ? next[0] : "";
            onChange?.(row.field, v);
          }}
          onOpenChange={onCatalogSelectOpenChange}
          renderOption={(option) => <span className="text-gray-900">{option.label}</span>}
          selectClassname="w-full"
          buttonSelectedClassname="!px-2 !text-xs !py-1.5"
        />
      </div>
    );
  }
  if (row.type === "select") {
    const rowValue = row.field ? values?.[row.field] || "" : row.value;
    const options = row.optionsKey ? (dynamicOptions[row.optionsKey] || []) : (row.options || []);
    const labelKey = row.optionsKey || (row.options === STATUS_OPTIONS ? "status" : "");
    const { allLabel, noneLabel } =
      SIDEBAR_SELECT_TRIGGER_LABELS[labelKey] || { allLabel: "", noneLabel: EMPTY_TEXT };
    const emptyTriggerLabel = EMPTY_TEXT;
    const selectedForFilter =
      rowValue !== "" && rowValue !== null && rowValue !== undefined ? [String(rowValue)] : [];
    const isSelectLoading =
      row.optionsKey === "categoryOptions" || row.optionsKey === "subcategoryOptions"
        ? Boolean(dynamicOptions.isCategorySettingsLoading)
        : row.optionsKey
          ? Boolean(dynamicOptions.isPresetSettingsLoading)
          : false;
    return (
      <div className="w-[210px] min-w-0">
        <ProductCatalogSelectFilter
          showAllNone={false}
          allLabel={emptyTriggerLabel}
          noneLabel={noneLabel}
          options={options}
          isLoading={isSelectLoading}
          selectedOptions={selectedForFilter}
          onChange={(next) => {
            if (!row.field) return;
            const v = Array.isArray(next) && next.length > 0 ? next[0] : "";
            onChange?.(row.field, v);
          }}
          onOpenChange={onCatalogSelectOpenChange}
          renderOption={(option) => <span className="text-gray-900">{option.label}</span>}
          selectClassname="w-full"
          buttonSelectedClassname="!px-2 !text-xs !py-1.5"
        />
      </div>
    );
  }
  if (row.type === "date") {
    const iso = formatCatalogDateForInput(values?.[row.field]);
    return (
      <div
        className="min-w-0 max-w-[210px] flex-1 pl-2 text-right"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DatePickerInput
          value={iso}
          min={'2018-01-01'}
          max={`${new Date().getFullYear() + 5}-12-31`}
          onChange={(next) => onChange?.(row.field, next)}
          placeholder="Pick a date"
          dateFormat="MM/dd/yyyy"
          iconPlacement="end"
          buttonPreset="sidebar"
          buttonClassName="!px-2 !text-xs"
          pickerClassName="!right-0"
          className="w-full"
        />
      </div>
    );
  }
  if (row.type === "textarea") {
    return (
      <div className="min-w-0 w-full flex-1 text-right">
        <SidebarDoubleClickTextarea row={row} values={values} onChange={onChange} item={item} />
      </div>
    );
  }
  if (row.type === "input") {
    return (
      <div className="min-w-0 max-w-[210px] flex-1 pl-2 text-right ">
        <SidebarInlineEditableInput row={row} values={values} onChange={onChange} item={item} />
      </div>
    );
  }
  if (row.type === "computed") {
    if (row.field === "total_lead_time_days") {
      const total = sumLeadTimeDays(values?.lead_time_days, values?.transit_lead_time_days);
      const hasMfg = values?.lead_time_days !== "" && values?.lead_time_days != null;
      const hasTransit = values?.transit_lead_time_days !== "" && values?.transit_lead_time_days != null;
      return (
        <p className="text-sm text-gray-700 text-right">
          {hasMfg || hasTransit ? String(total) : EMPTY_TEXT}
        </p>
      );
    }
    const length = Number(values?.unit_length || 0);
    const width = Number(values?.unit_width || 0);
    const height = Number(values?.unit_height || 0);
    const hasAllValues =
      values?.unit_length !== "" && values?.unit_width !== "" && values?.unit_height !== "";
    const cubicInches = hasAllValues ? (length * width * height) : null;
    return (
      <p className="text-sm text-gray-700 text-right">
        {cubicInches === null ? EMPTY_TEXT : cubicInches.toFixed(2)}
      </p>
    );
  }
  if (row.type === "switcher") {
    const checked = Boolean(values?.[row.field]);
    const readOnly = Boolean(row.readOnly);
    return (
      <label className={`relative inline-flex items-center ${readOnly ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={readOnly}
          onChange={(event) => {
            if (readOnly || !row.field) return;
            onChange?.(row.field, event.target.checked);
          }}
          className="sr-only peer"
        />
        <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary peer-disabled:opacity-60" />
      </label>
    );
  }
  if (row.type === "channel-sku") {
    return (
      <ChannelSkuPicker
        row={row}
        values={values}
        item={item}
        onChange={onChange}
        dynamicOptions={dynamicOptions}
        onOpenChange={onCatalogSelectOpenChange}
      />
    );
  }
  if (row.type === "packaging-sku") {
    return (
      <PackagingSkuPicker
        row={row}
        values={values}
        item={item}
        onChange={onChange}
        onOpenChange={onCatalogSelectOpenChange}
      />
    );
  }
  if (row.type === "catalog-sku") {
    return (
      <div className="min-w-0 max-w-[210px] flex-1 pl-2 text-right">
        <CatalogSkuPicker row={row} values={values} item={item} onChange={onChange} />
      </div>
    );
  }
  const rowValue = row.field ? item?.[row.field] : row.value;
  const isEmptyValue = rowValue === null || rowValue === undefined || rowValue === "" || rowValue === "-";
  let displayValue = isEmptyValue ? EMPTY_TEXT : rowValue;
  if (row.isCurrency && !isEmptyValue) {
    const asText = String(rowValue);
    displayValue = asText.startsWith("$") ? asText : `$${asText}`;
  } else if (
    !isEmptyValue &&
    (row.field === "shopify_product_id" || row.field === "shopify_variant_id")
  ) {
    displayValue = formatShopifyGidForDisplay(rowValue);
  }
  return (
    <p className="text-xs text-gray-700 text-right">{displayValue}</p>
  );
};

const BARCODE_DOWNLOAD_STATUSES = ["ACTIVE", "PLANNED"];

const hasCatalogBarcode = (barcode) => {
  const value = String(barcode ?? "").trim();
  return value !== "" && value !== "-";
};

const CatalogBarcodePanel = ({
  item,
  groupVariants,
  groupVariantsLoading,
  singleBarcodeLoading,
  groupBarcodesLoading,
  onDownloadSingle,
  onDownloadGroup,
}) => {
  const barcode = item?.barcode;
  const sku = item?.supply_sku;
  const groupName = item?.product_name && item.product_name !== EMPTY_TEXT ? item.product_name : "Group";
  const eligibleGroupBarcodes = useMemo(
    () =>
      (groupVariants || []).filter(
        (variant) =>
          !variant?.is_channel_linked &&
          BARCODE_DOWNLOAD_STATUSES.includes(String(variant?.status || "").trim()) &&
          hasCatalogBarcode(variant?.barcode)
      ),
    [groupVariants]
  );
  const eligibleCount = eligibleGroupBarcodes.length;
  const showBarcode = hasCatalogBarcode(barcode);
  const groupIsAvailable = Boolean(item?.group_id);

  if (!showBarcode) {
    return (
      <div className="w-full border border-gray-200 rounded-md p-4 bg-white">
        <p className="w-full text-xs text-gray-500 text-center py-2">No barcode available</p>
      </div>
    );
  }

  return (
    <div className="w-full border border-gray-200 rounded-md p-4 bg-white">
      {showBarcode ? (
        <>
          <p className="text-center text-xl font-semibold text-gray-900">
            {sku && sku !== EMPTY_TEXT ? sku : EMPTY_TEXT}
          </p>
          <div className="flex justify-center mb-2">
            <Barcode
              value={barcode}
              width={3}
              height={90}
              format="CODE128"
              displayValue
              font="monospace"
              textAlign="center"
              textPosition="bottom"
              textMargin={6}
              fontSize={20}
              background="#ffffff"
              lineColor="#000000"
            />
          </div>
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center mb-4">No barcode available for this SKU</p>
      )}
      <div className="flex gap-2">
        {showBarcode && (
          <>
            <button
              type="button"
              onClick={onDownloadSingle}
              disabled={singleBarcodeLoading || groupBarcodesLoading || groupVariantsLoading}
              className="relative flex flex-1 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Download barcode as PDF"
            >
              {singleBarcodeLoading && (
                <div className="absolute -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2">
                  <LoaderSpinner height="16" width="16" colorSpinner="black" border="2" />
                </div>
              )}
              <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Download Barcode</span>
            </button>
            {groupIsAvailable && (
              <button
                type="button"
                onClick={() => onDownloadGroup(groupName, eligibleCount)}
                disabled={groupBarcodesLoading || groupVariantsLoading || singleBarcodeLoading}
                className="relative flex flex-1 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Download all product barcodes for group"
              >
                {(groupBarcodesLoading || groupVariantsLoading) && (
                  <div className="absolute -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2">
                    <LoaderSpinner height="16" width="16" colorSpinner="black" border="2" />
                  </div>
                )}
                <CubeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">All Product Barcodes</span>
                {eligibleCount > 0 && (
                  <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                    {eligibleCount}
                  </span>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const ProductCatalogProductSidebar = ({
  isOpen,
  onClose,
  item,
  onSave,
  isSaving = false,
  onDeleteClick,
  isDeleteProductLoading = () => false,
  productIdToDelete = "",
  catalogView = "default",
  onRestoreClick,
  isRestoreProductLoading = () => false,
  productIdToRestore = "",
  onHistoryClick,
}) => {
  const dispatch = useDispatch();
  const { categorySettingsItems, presetSettingsByType, containerSettingsItems, channelProductsItems, loadingKeys } = useSelector((state) => state.productCatalog);
  const {
    vendorsToSelect,
    loadingKeys: groupLoadingKeys,
    variantsByGroup,
    loading: groupLoading,
  } = useSelector((state) => state.productGroup);
  const { loading: productLoading, loadingKeys: productLoadingKeys } = useSelector((state) => state.products);
  const [shopifySyncConfirm, setShopifySyncConfirm] = useState(null);
  const [shopifySyncingField, setShopifySyncingField] = useState(null);
  const [values, setValues] = useState({
    brand_pillar_id: "",
    demand_tier_id: "",
    intent_id: "",
    entry_engine_id: "",
    status: "",
    unit_weight_lbs: "",
    unit_length: "",
    unit_width: "",
    unit_height: "",
    category_group_id: "",
    subcategory_variant_id: "",
    is_packaging: false,
    is_supply: false,
    is_virtual: false,
    sell_on_shopify: false,
    sell_on_amazon: false,
    fbm_channel_product_id: [],
    fba_channel_product_id: [],
    packaging_product_id: [],
    amazon_fbm_sku: "",
    amazon_fba_sku: "",
    packaging_sku: "",
    forecastable: false,
    needs_review: false,
    lock_flag: false,
    replacement_exit: false,
    replacement_product_id: "",
    condition_of_product_id: "",
    replaces_sku: "",
    condition_of_sku: "",
    vendor_id: "",
    lead_time_days: "",
    transit_lead_time_days: "",
    moq: "",
    container_group_id: "",
    container_qty: "",
    carton_qty: "",
    launch_date: "",
    sunset_date: "",
    inactive_date: "",
    color: "",
    material: "",
    shopify_product_id: "",
  });

  /** While a catalog select is open, lift this row above following rows (drawer is scroll-y; later siblings paint on top otherwise). */
  const [stackedSelectRowKey, setStackedSelectRowKey] = useState(null);

  const isCategorySettingsLoading = loadingKeys.includes(ProductCatalogLoadingKeys.fetchCategorySettings);
  const isPresetSettingsLoading = loadingKeys.includes(ProductCatalogLoadingKeys.fetchPresetSettings);
  const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);

  const vendorOptions = useMemo(
    () =>
      (vendorsToSelect || []).map((v) => ({
        label: v.label,
        value: String(v.value),
      })),
    [vendorsToSelect]
  );
  const isShopifyMetafieldsSyncLoading = loadingKeys.includes(ProductCatalogLoadingKeys.syncMetafieldsToShopify);

  useEffect(() => {
    if (!isOpen) setStackedSelectRowKey(null);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setShopifySyncConfirm(null);
    setShopifySyncingField(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchProductCatalogCategorySettings());
    dispatch(fetchProductCatalogPresetSettings());
    dispatch(getSuppliers({}));
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!item?.barcode || item?.barcode === "-") return;
    dispatch(fetchProductCatalogChannelProducts({
      barcode: item.barcode,
      productId: item?.id,
    }));
  }, [dispatch, isOpen, item?.barcode, item?.id]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(clearVariantsByGroup({}));
      return;
    }
    if (!item?.group_id) {
      dispatch(clearVariantsByGroup({}));
      return;
    }
    dispatch(getVariantsByGroup({ id: item.group_id }));
  }, [dispatch, isOpen, item?.group_id]);

  const singleBarcodeDownloadIsLoading = () =>
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.generateBarcode);
  const groupBarcodesDownloadIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.downloadBarcodes);
  const groupVariantsLoading =
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariantsByGroup);

  const handleDownloadSingleBarcode = async () => {
    if (!hasCatalogBarcode(item?.barcode)) return;
    try {
      const resultAction = await dispatch(
        printProductBarcode({
          productData: {
            productId: item?.id,
            productName: item?.variant_name && item.variant_name !== EMPTY_TEXT ? item.variant_name : item?.product_name,
            sku: item?.supply_sku,
            barcode: item?.barcode,
          },
        })
      );
      const res = unwrapResult(resultAction);
      const newTab = window.open(res.url, "_blank");
      if (newTab) {
        newTab.focus();
      }
    } catch (_) {
      /* toast handled in slice */
    }
  };

  const handleDownloadGroupBarcodes = async (groupName, eligibleCount) => {
    if (!item?.group_id || groupVariantsLoading) return;
    if (eligibleCount <= 0) {
      toast.warn(
        `No Active or Planned barcodes found for "${groupName}". All variants may be Sunset or Inactive.`,
        { position: "bottom-right" }
      );
      return;
    }
    try {
      const resultAction = await dispatch(
        downloadAllBarcodes({
          groupId: item.group_id,
          statuses: BARCODE_DOWNLOAD_STATUSES,
        })
      );
      const res = unwrapResult(resultAction);
      const newTab = window.open(res.url, "_blank");
      if (newTab) {
        newTab.focus();
      }
    } catch (_) {
      /* toast handled in slice */
    }
  };

  const categoryOptions = useMemo(
    () =>
      (categorySettingsItems || []).map((group) => ({
        label: group.name,
        value: String(group.id),
      })),
    [categorySettingsItems]
  );

  const subcategoryOptions = useMemo(() => {
    const selectedCategoryId = Number(values.category_group_id);
    if (!selectedCategoryId) return [];
    const currentGroup = (categorySettingsItems || []).find((group) => Number(group.id) === selectedCategoryId);
    return (currentGroup?.variants || []).map((variant) => ({
      label: variant.name,
      value: String(variant.id),
    }));
  }, [categorySettingsItems, values.category_group_id]);

  const entryEngineOptions = useMemo(
    () =>
      (presetSettingsByType?.entry_engine || []).map((preset) => ({
        label: preset.name,
        value: String(preset.id),
      })),
    [presetSettingsByType?.entry_engine]
  );
  const brandPillarOptions = useMemo(
    () =>
      (presetSettingsByType?.brand_pillar || []).map((preset) => ({
        label: preset.name,
        value: String(preset.id),
      })),
    [presetSettingsByType?.brand_pillar]
  );
  const demandTierOptions = useMemo(
    () =>
      (presetSettingsByType?.demand_tier || []).map((preset) => ({
        label: preset.name,
        value: String(preset.id),
      })),
    [presetSettingsByType?.demand_tier]
  );
  const intentOptions = useMemo(
    () =>
      (presetSettingsByType?.intent || []).map((preset) => ({
        label: preset.name,
        value: String(preset.id),
      })),
    [presetSettingsByType?.intent]
  );
  const containerGroupOptions = useMemo(
    () =>
      (containerSettingsItems || []).map((group) => ({
        label: group.name,
        value: String(group.id),
      })),
    [containerSettingsItems]
  );

  const getSidebarSyncOption = (field) => {
    if (field === "demand_tier_id") {
      return (demandTierOptions || []).find((option) => option.value === String(values.demand_tier_id || ""));
    }
    if (field === "intent_id") {
      return (intentOptions || []).find((option) => option.value === String(values.intent_id || ""));
    }
    return null;
  };

  const canShowSidebarShopifySync = (field) => {
    if (!SIDEBAR_SHOPIFY_SYNC_FIELDS.has(field)) return false;
    if (!hasSidebarShopifyLinks(item)) return false;
    const option = getSidebarSyncOption(field);
    return true;
    // return Boolean(option?.label) && !isSidebarShopifyNoneLabel(option.label);
  };

  const openSidebarShopifySyncConfirm = (field) => {
    const option = getSidebarSyncOption(field);
    // if (!option?.label) return;
    setShopifySyncConfirm({
      field,
      label: option?.label || null,
    });
  };

  const cancelSidebarShopifySync = () => {
    setShopifySyncConfirm(null);
  };

  const confirmSidebarShopifySync = async () => {
    if (!shopifySyncConfirm || !item?.id) return;
    const field =
      shopifySyncConfirm.field === "demand_tier_id" ? "product_tier" : "product_intent";
    setShopifySyncingField(shopifySyncConfirm.field);
    try { 
      await dispatch(
        syncMetafieldsToShopify({
          productsToShopifySync: [
            {
              product_id: item.id,
              shopify_product_id: item.shopify_product_id,
              shopify_variant_id: item.shopify_variant_id,
            },
          ],
          fieldToUpdate: {
            key: field,
            value: shopifySyncConfirm.label,
          },
        })
      ).unwrap();
      setShopifySyncConfirm(null);
    } catch (error) {
      console.log(error)
    } finally {
      setShopifySyncingField(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setValues({
      brand_pillar_id: item?.brand_pillar_id ? String(item.brand_pillar_id) : "",
      demand_tier_id: item?.demand_tier_id ? String(item.demand_tier_id) : "",
      intent_id: item?.intent_id ? String(item.intent_id) : "",
      entry_engine_id: item?.entry_engine_id ? String(item.entry_engine_id) : "",
      status: item?.status && item.status !== "-" ? item.status : "",
      category_group_id: item?.category_group_id ? String(item.category_group_id) : "",
      subcategory_variant_id: item?.subcategory_variant_id ? String(item.subcategory_variant_id) : "",
      is_packaging: Boolean(item?.is_packaging),
      is_supply: Boolean(item?.is_supply),
      unit_weight_lbs:
        item?.unit_weight_lbs !== undefined && item?.unit_weight_lbs !== null && item?.unit_weight_lbs !== "-"
          ? String(item.unit_weight_lbs)
          : "",
      unit_length:
        item?.unit_length !== undefined && item?.unit_length !== null && item?.unit_length !== "-"
          ? String(item.unit_length)
          : "",
      unit_width:
        item?.unit_width !== undefined && item?.unit_width !== null && item?.unit_width !== "-"
          ? String(item.unit_width)
          : "",
      unit_height:
        item?.unit_height !== undefined && item?.unit_height !== null && item?.unit_height !== "-"
          ? String(item.unit_height)
          : "",
      is_virtual: Boolean(item?.is_virtual),
      sell_on_shopify: Boolean(item?.sell_on_shopify),
      sell_on_amazon: Boolean(item?.sell_on_amazon),
      fbm_channel_product_id: parseChannelProductIds(item?.fbm_channel_product_id),
      fba_channel_product_id: parseChannelProductIds(item?.fba_channel_product_id),
      packaging_product_id: parseChannelProductIds(item?.packaging_product_id),
      amazon_fbm_sku: item?.amazon_fbm_sku && item.amazon_fbm_sku !== "-" ? String(item.amazon_fbm_sku) : "",
      amazon_fba_sku: item?.amazon_fba_sku && item.amazon_fba_sku !== "-" ? String(item.amazon_fba_sku) : "",
      packaging_sku: item?.packaging_sku && item.packaging_sku !== "-" ? String(item.packaging_sku) : "",
      forecastable: Boolean(item?.forecastable),
      needs_review: Boolean(item?.needs_review),
      lock_flag: Boolean(item?.lock_flag),
      replacement_exit: Boolean(item?.replacement_exit),
      replacement_product_id: item?.replacement_product_id ? String(item.replacement_product_id) : "",
      condition_of_product_id: item?.condition_of_product_id ? String(item.condition_of_product_id) : "",
      replaces_sku: item?.replaces_sku && item.replaces_sku !== "-" ? String(item.replaces_sku) : "",
      condition_of_sku: item?.condition_of_sku && item.condition_of_sku !== "-" ? String(item.condition_of_sku) : "",
      vendor_id:
        item?.vendor_id !== undefined && item?.vendor_id !== null && item?.vendor_id !== ""
          ? String(item.vendor_id)
          : "",
      lead_time_days:
        item?.lead_time_days !== undefined &&
        item?.lead_time_days !== null &&
        item?.lead_time_days !== "-"
          ? String(item.lead_time_days)
          : "",
      transit_lead_time_days:
        item?.transit_lead_time_days !== undefined &&
        item?.transit_lead_time_days !== null &&
        item?.transit_lead_time_days !== "-"
          ? String(item.transit_lead_time_days)
          : "",
      moq:
        item?.moq !== undefined && item?.moq !== null && item?.moq !== "-"
          ? String(item.moq)
          : "",
      container_group_id: item?.container_group_id ? String(item.container_group_id) : "",
      container_qty:
        item?.container_qty !== undefined &&
        item?.container_qty !== null &&
        item?.container_qty !== "-"
          ? String(item.container_qty)
          : "",
      carton_qty:
        item?.carton_qty !== undefined &&
        item?.carton_qty !== null &&
        item?.carton_qty !== "-"
          ? String(item.carton_qty)
          : "",
      launch_date: formatCatalogDateForInput(item?.launch_date),
      sunset_date: formatCatalogDateForInput(item?.sunset_date),
      inactive_date: formatCatalogDateForInput(item?.inactive_date),
      color: item?.color && item.color !== "-" ? String(item.color) : "",
      material:
        item?.material !== undefined &&
        item?.material !== null &&
        item.material !== "" &&
        item.material !== "-" &&
        item.material !== EMPTY_TEXT
          ? String(item.material)
          : "",
      shopify_product_id:
        item?.shopify_product_id !== undefined &&
        item?.shopify_product_id !== null &&
        item.shopify_product_id !== "-" &&
        item.shopify_product_id !== EMPTY_TEXT
          ? String(item.shopify_product_id)
          : "",
    });
  }, [isOpen, item]);

  const sellOnShopifyOrAmazon = item?.sell_on_shopify || item?.sell_on_amazon;

  const isDeletedProductsCatalogView = catalogView === "deleted_products";

  return (
    <>
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title=""
      closeButton={<DrawerCloseButton onClose={onClose} />}
      mobileTopOffset
      headerContent={(
        <div className="w-full">
          <div className="flex flex-col gap-2">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <p className="min-w-0 truncate text-base font-semibold text-gray-900">
                  {item?.canonical_product_name || "Product Details"}
              </p>
              <button
                type="button"
                className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                onClick={() => onHistoryClick?.(item)}
              >
                History
              </button>
              <CatalogStatusChip
                status={item?.status}
                label={enumInventoryPhasedStatus[item?.status] || EMPTY_TEXT}
              />
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <SupplySku
                sku={item?.supply_sku || EMPTY_TEXT}
                className="block max-w-full shrink-0 truncate"
              />
              {item?.color && item.color !== EMPTY_TEXT ? (
                <span className="min-w-0 truncate text-[12px] text-gray-500">{item.color}</span>
              ) : null}
            </div>
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="flex min-w-0 items-baseline gap-1">
                <span className="truncate text-2xl font-semibold text-gray-900">
                  {item?.msrp && item.msrp !== EMPTY_TEXT ? item.msrp : EMPTY_TEXT}
                </span>
                <span className="shrink-0 text-xs uppercase tracking-wide text-gray-500">MSRP</span>
              </div>
              {sellOnShopifyOrAmazon && (
                <div className="flex shrink-0 items-center gap-1.5">
                  {item?.sell_on_shopify && (
                    <div className="flex items-center gap-1 rounded-md border border-emerald-500/30 px-1.5 py-0.5 text-emerald-600">
                      <ShoppingBasketIcon className="h-3 w-3 text-green-500" />
                      <span className="text-[10px]">Shopify</span>
                    </div>
                  )}
                  {item?.sell_on_amazon && (
                    <div className="flex items-center gap-1 rounded-md border border-amber-500/30 px-1.5 py-0.5 text-amber-600">
                      <CubeIcon className="h-3 w-3 text-amber-600" />
                      <span className="text-[10px]">Amazon</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-1">
              {isDeletedProductsCatalogView ? (
                <button
                  type="button"
                  className="flex flex-row items-center gap-2 rounded border border-black bg-black px-3 py-1 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                  title={`Restore product ${item?.canonical_product_name || ""}`}
                  onClick={() => onRestoreClick?.(item)}
                >
                  <span className="text-xs">Restore Product</span>
                  {isRestoreProductLoading() && productIdToRestore === item?.id ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="flex flex-row items-center gap-1.5 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-50"
                  title={`Delete product ${item?.canonical_product_name || ""}`}
                  onClick={() => onDeleteClick?.(item)}
                >
                  <span className="text-xs">Delete Product</span>
                  {isDeleteProductLoading() && productIdToDelete === item?.id ? (
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      width="572px"
      showActionButtons
      submitButtonText="Save"
      onSubmit={() => {
        if (isCatalogSidebarSaveRedundant(values, item)) {
          return;
        }
        const editable = { ...values };
        delete editable.sell_on_shopify;
        delete editable.sell_on_amazon;
        delete editable.supplier;
        onSave?.({
          ...editable,
          category_group_id: values.category_group_id || null,
          subcategory_variant_id: values.subcategory_variant_id || null,
        });
      }}
      submitDisabled={isSaving}
    >
      <div className="px-6 py-5 flex flex-col gap-6">
        {isCategorySettingsLoading && (
          <p className="text-xs text-gray-400">Loading categories...</p>
        )}
        {DETAILS_SECTIONS.map((section) => (
          <section key={section.title} className="flex flex-col gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              {section.title}
            </p>
            <div className="bg-white border-b border-gray-300 pb-4">
              {section.rows
                .filter((row) => !row.showWhen || values[row.showWhen])
                .map((row) => {
                const rowKey = `${section.title}-${row.label || row.type}`;
                if (row.type === "barcode-panel") {
                  return (
                    <div key={rowKey} className="py-2">
                      <CatalogBarcodePanel
                        item={item}
                        groupVariants={variantsByGroup}
                        groupVariantsLoading={groupVariantsLoading}
                        singleBarcodeLoading={singleBarcodeDownloadIsLoading()}
                        groupBarcodesLoading={groupBarcodesDownloadIsLoading()}
                        onDownloadSingle={handleDownloadSingleBarcode}
                        onDownloadGroup={handleDownloadGroupBarcodes}
                      />
                    </div>
                  );
                }
                const showSidebarShopifySync = canShowSidebarShopifySync(row.field);
                return (
                  <div
                    key={rowKey}
                    className={`flex gap-2 px-0 py-1.5 ${
                      row.type === "textarea"
                        ? "group items-start rounded-lg transition-colors"
                        : row.type === "input" || row.type === "date" || row.type === "catalog-sku"
                          ? "group items-center rounded-lg transition-colors hover:bg-gray-100"
                          : "items-center"
                    } ${row.showWhen ? "border-l border-gray-200 pl-3 rounded-none hover:bg-white" : ""} ${stackedSelectRowKey === rowKey ? "relative z-[80]" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <p
                          className={`truncate text-xs font-normal text-gray-500 ${
                            section.title === "Flags" && !row.showWhen ? "!font-bold !text-primary" : ""
                          }`}
                        >
                          {row.label}
                        </p>
                        {row.labelInfoTip ? <CatalogInfoTip text={row.labelInfoTip} /> : null}
                      </div>
                      {row.description && (
                        <p className="text-[10px] text-gray-400">{row.description}</p>
                      )}
                    </div>
                    <div
                      className={`flex min-w-0 max-w-[62%] flex-1 justify-end gap-1 ${
                        row.type === "textarea" ? "items-start" : "items-center"
                      }`}
                    >
                      {showSidebarShopifySync ? (
                        <SidebarShopifySyncButton
                          isLoading={isShopifyMetafieldsSyncLoading && shopifySyncingField === row.field}
                          onClick={() => openSidebarShopifySyncConfirm(row.field)}
                        />
                      ) : null}
                      <ValueField
                        row={row}
                        values={values}
                        item={item}
                        onCatalogSelectOpenChange={(open) => {
                          setStackedSelectRowKey(open ? rowKey : null);
                        }}
                        dynamicOptions={{
                          categoryOptions,
                          subcategoryOptions,
                          entryEngineOptions,
                          brandPillarOptions,
                          demandTierOptions,
                          intentOptions,
                          containerGroupOptions,
                          channelProductOptions: channelProductsItems,
                          vendorOptions,
                          vendorSelectLoading,
                          isCategorySettingsLoading,
                          isPresetSettingsLoading,
                        }}
                        onChange={(field, nextValue) => {
                          setValues((prev) => {
                            if (field === "category_group_id") {
                              return {
                                ...prev,
                                category_group_id: nextValue,
                                subcategory_variant_id: "",
                              };
                            }
                            if (field === "replacement_exit" && !nextValue) {
                              return {
                                ...prev,
                                replacement_exit: false,
                                replacement_product_id: "",
                                replaces_sku: "",
                              };
                            }
                            return {
                              ...prev,
                              [field]: nextValue,
                            };
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Drawer>
    <ConfrimModal
      isOpen={Boolean(shopifySyncConfirm)}
      isLoading={Boolean(shopifySyncingField)}
      title={
        shopifySyncConfirm
          ? `Sync ${shopifySyncConfirm.field === "demand_tier_id" ? "demand tier" : "intent"} to Shopify for ${String(item?.supply_sku || "").trim() || "this product"}?`
          : ""
      }
      confirmButtonTitle="Sync"
      submitClick={confirmSidebarShopifySync}
      cancelClick={cancelSidebarShopifySync}
      size="max-w-lg"
      testId="product-catalog-sidebar-metafield-sync-confirm"
    />
    </>
  );
};
